import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOnboarding } from '../../context/OnboardingContext';
import OnboardingLayout, { CancelButton } from './OnboardingLayout';

// Funciones de validación
const validatePhone = (phone: string): boolean => {
  const phoneRegex = /^3\d{9}$/; // Debe comenzar con 3 y tener 10 dígitos en total
  return phoneRegex.test(phone);
};

const validateEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/; // Validación básica de email con @
  return emailRegex.test(email);
};

const CompanyDetails: React.FC = () => {
  const navigate = useNavigate();
  const { companyData, updateCompanyData, setCurrentStep } = useOnboarding();
  
  // Estados para los campos del formulario
  const [companyName, setCompanyName] = useState(companyData.name || '');
  const [legalRepName, setLegalRepName] = useState('');
  const [legalRepId, setLegalRepId] = useState('');
  const [phone, setPhone] = useState(companyData.phone || '');
  const [email, setEmail] = useState(companyData.email || '');
  const [employeeCount, setEmployeeCount] = useState('');
  const [yearsExperience, setYearsExperience] = useState('');
  
  // Estados para errores de validación
  const [phoneError, setPhoneError] = useState('');
  const [emailError, setEmailError] = useState('');
  
  // Validación para habilitar el botón continuar
  const [isFormValid, setIsFormValid] = useState(false);
  
  // Verificar si la categoría está seleccionada, si no, redirigir
  useEffect(() => {
    if (!companyData.category) {
      navigate('/onboarding/category-selection');
    }
  }, [companyData.category, navigate]);
  
  // Validar el teléfono cuando cambia
  useEffect(() => {
    if (phone.trim() !== '') {
      if (!validatePhone(phone)) {
        setPhoneError('El teléfono debe comenzar con 3 y tener 10 dígitos');
      } else {
        setPhoneError('');
      }
    } else {
      setPhoneError('');
    }
  }, [phone]);
  
  // Validar el email cuando cambia
  useEffect(() => {
    if (email.trim() !== '') {
      if (!validateEmail(email)) {
        setEmailError('Ingrese un correo electrónico válido');
      } else {
        setEmailError('');
      }
    } else {
      setEmailError('');
    }
  }, [email]);
  
  // Validar el formulario cuando cambian los campos
  useEffect(() => {
    const isValid = 
      companyName.trim() !== '' && 
      legalRepName.trim() !== '' && 
      legalRepId.trim() !== '' && 
      phone.trim() !== '' && 
      validatePhone(phone) && 
      email.trim() !== '' && 
      validateEmail(email) && 
      employeeCount.trim() !== '' && 
      yearsExperience.trim() !== '';
    
    setIsFormValid(isValid);
  }, [companyName, legalRepName, legalRepId, phone, email, employeeCount, yearsExperience]);
  
  const handleBack = () => {
    setCurrentStep(3);
    navigate('/onboarding/profile-documents');
  };

  // Manejar el envío del formulario
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Actualizar los datos en el contexto
    updateCompanyData({
      name: companyName,
      email: email,
      phone: phone,
      legalRepName: legalRepName,
      legalRepId: legalRepId,
      employeeCount: parseInt(employeeCount),
      yearsExperience: parseInt(yearsExperience)
    });
    
    setCurrentStep(5); // Avanzar al siguiente paso
    navigate('/onboarding/company-history'); // Navegar a la página de historia de la empresa
  };
  
  return (
    <OnboardingLayout
      title="¡Queremos conocerte mejor!"
      subtitle="Comparte con nosotros la información clave de tu empresa. Esto nos permitirá presentarte de forma profesional ante tus futuros clientes y ayudarte a crecer dentro de XERVIX"
      showBackButton={true}
      onBack={handleBack}
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Nombre de la empresa */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-xervix-text-primary dark:text-xervix-text-primary-dark">
              Nombre de la empresa
            </label>
            <input
              type="text"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              className="text-xervix-text-primary dark:text-xervix-text-primary-dark w-full px-4 py-3 rounded-lg bg-xervix-bg-tertiary dark:bg-xervix-bg-tertiary-dark border border-xervix-border dark:border-xervix-border-dark focus:outline-none focus:ring-2 focus:ring-xervix-primary"
              placeholder="Ingrese nombre de la empresa"
            />
          </div>
          
          {/* Teléfono de contacto */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-xervix-text-primary dark:text-xervix-text-primary-dark">
              Teléfono de contacto
            </label>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className={`text-xervix-text-primary dark:text-xervix-text-primary-dark w-full px-4 py-3 rounded-lg bg-xervix-bg-tertiary dark:bg-xervix-bg-tertiary-dark border ${phoneError ? 'border-red-500' : 'border-xervix-border dark:border-xervix-border-dark'} focus:outline-none focus:ring-2 focus:ring-xervix-primary`}
              placeholder="Ingrese número telefónico"
            />
            {phoneError && <p className="text-red-500 text-xs mt-1">{phoneError}</p>}
          </div>
          
          {/* Nombre representante legal */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-xervix-text-primary dark:text-xervix-text-primary-dark">
              Nombre representante legal
            </label>
            <input
              type="text"
              value={legalRepName}
              onChange={(e) => setLegalRepName(e.target.value)}
              className="text-xervix-text-primary dark:text-xervix-text-primary-dark w-full px-4 py-3 rounded-lg bg-xervix-bg-tertiary dark:bg-xervix-bg-tertiary-dark border border-xervix-border dark:border-xervix-border-dark focus:outline-none focus:ring-2 focus:ring-xervix-primary"
              placeholder="Ingrese nombre de representante legal"
            />
          </div>
          
          {/* Correo electrónico */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-xervix-text-primary dark:text-xervix-text-primary-dark">
              Correo electrónico
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={`text-xervix-text-primary dark:text-xervix-text-primary-dark w-full px-4 py-3 rounded-lg bg-xervix-bg-tertiary dark:bg-xervix-bg-tertiary-dark border ${emailError ? 'border-red-500' : 'border-xervix-border dark:border-xervix-border-dark'} focus:outline-none focus:ring-2 focus:ring-xervix-primary`}
              placeholder="Ingrese Correo electrónico"
            />
            {emailError && <p className="text-red-500 text-xs mt-1">{emailError}</p>}
          </div>
          
          {/* NIT o cédula del representante */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-xervix-text-primary dark:text-xervix-text-primary-dark">
              NIT o cédula del representante
            </label>
            <input
              type="number"
              value={legalRepId}
              onChange={(e) => setLegalRepId(e.target.value)}
              className="text-xervix-text-primary dark:text-xervix-text-primary-dark w-full px-4 py-3 rounded-lg bg-xervix-bg-tertiary dark:bg-xervix-bg-tertiary-dark border border-xervix-border dark:border-xervix-border-dark focus:outline-none focus:ring-2 focus:ring-xervix-primary"
              placeholder="Ingrese nit o cédula"
            />
          </div>
          
          {/* Número de empleados y Años de experiencia en una sola fila */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-xervix-text-primary dark:text-xervix-text-primary-dark">
                Numero de empleados
              </label>
              <input
                type="number"
                value={employeeCount}
                onChange={(e) => setEmployeeCount(e.target.value)}
                className="text-xervix-text-primary dark:text-xervix-text-primary-dark w-full px-4 py-3 rounded-lg bg-xervix-bg-tertiary dark:bg-xervix-bg-tertiary-dark border border-xervix-border dark:border-xervix-border-dark focus:outline-none focus:ring-2 focus:ring-xervix-primary"
                placeholder="# de empleados"
                min="1"
              />
            </div>
            
            <div className="space-y-2">
              <label className="block text-sm font-medium text-xervix-text-primary dark:text-xervix-text-primary-dark">
                Años de experiencia
              </label>
              <input
                type="number"
                value={yearsExperience}
                onChange={(e) => setYearsExperience(e.target.value)}
                className="text-xervix-text-primary dark:text-xervix-text-primary-dark w-full px-4 py-3 rounded-lg bg-xervix-bg-tertiary dark:bg-xervix-bg-tertiary-dark border border-xervix-border dark:border-xervix-border-dark focus:outline-none focus:ring-2 focus:ring-xervix-primary"
                placeholder="Años de experiencia"
                min="0"
              />
            </div>
          </div>
        </div>
        
        <div className="flex justify-center gap-4 mt-8">
          <CancelButton />
          <button
            type="submit"
            disabled={!isFormValid}
            className={`px-8 py-3 rounded-md text-white font-medium transition-colors ${
              isFormValid 
                ? 'bg-xervix-primary text-white hover:bg-opacity-90' 
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            Continuar
          </button>
        </div>
      </form>
    </OnboardingLayout>
  );
};

export default CompanyDetails;
